import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import 'bootstrap-icons/font/bootstrap-icons.css';
import AuthPageShell from './shared/AuthPageShell';
import PasswordInput from './shared/PasswordInput';
import AuthAlert from './shared/AuthAlert';
import './css/Login.css';

const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

const OrgLogin = ({ handleLogin }) => {
  const location = useLocation();
  const [formData, setFormData] = useState({ email: location.state?.email || '', password: '' });
  const [message, setMessage] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // Resume session only when a JWT is present (API calls require Bearer token).
  useEffect(() => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('orgId');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    if (token && orgId && username && role === 'org') {
      handleLogin({ token, orgId, username, role });
      navigate('/org/dashboard', { replace: true });
    }
  }, [navigate, handleLogin]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleCaptchaChange = (value) => setCaptchaValue(value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaValue) {
      setMessage('Please complete the reCAPTCHA');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API_URL}/auth/org-login`, {
        ...formData,
        captcha: captchaValue,
      });

      const { token, username, role, orgId, refreshToken } = res.data;

      if (!token) {
        setMessage('Login failed — token not received.');
        return;
      }

      localStorage.setItem('token', token);
      localStorage.setItem('orgId', orgId || '');
      localStorage.setItem('username', username || '');
      localStorage.setItem('role', role || 'org');
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('orgId', orgId || '');
      sessionStorage.setItem('username', username || '');
      sessionStorage.setItem('role', role || 'org');
      sessionStorage.removeItem('impersonating');

      handleLogin({ token, orgId, username, role: role || 'org' });
      setMessage('Login successful');
      navigate('/org/dashboard', { replace: true });
    } catch (err) {
      setMessage(err?.response?.data?.msg || 'Invalid credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageShell title="Organisation Login">
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3">
          <input
            type="email"
            name="email"
            className="form-control"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <PasswordInput value={formData.password} onChange={handleChange} />

        <div className="login-recaptcha-wrapper">
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'your_site_key_here'}
            onChange={handleCaptchaChange}
          />
        </div>

        <button type="submit" className="btn btn-warning btn-block mt-3" disabled={isSubmitting}>
          {isSubmitting ? 'Logging in...' : 'Login'}
        </button>

        <AuthAlert message={message} variant={message === 'Login successful' ? 'success' : 'danger'} />

        <div className="text-center mt-3">
          <a href="/org-forgot-password">Forgot password?</a>
        </div>
      </form>
    </AuthPageShell>
  );
};

export default OrgLogin;
